/**
 * Ticker Sentiment Service
 *
 * Calculates sentiment for a specific ticker within a group by analyzing messages
 * that mention that ticker. Uses keyword-based analysis (bullish vs bearish keywords).
 *
 * This provides per-ticker sentiment breakdown (vs group-wide sentiment from dailySentimentSnapshotService)
 */

import { supabase } from '@/services/supabase/supabaseClient';
import { Message } from '@/services/chat/chatService';

export interface TickerSentiment {
  ticker: string;
  bullishCount: number;
  bearishCount: number;
  neutralCount: number;
  totalMentions: number;
  sentiment: 'bullish' | 'bearish' | 'neutral';
  confidenceScore: number; // 0-100
  recentMessages: Message[];
  timeframe: string;
}

class TickerSentimentService {
  private readonly BULLISH_KEYWORDS = [
    'buy',
    'bullish',
    'moon',
    'rocket',
    'up',
    'gain',
    'profit',
    'long',
    'calls',
    'breakout',
    'surge',
    'rally',
    'boom',
    'strong',
    'growth',
  ];

  private readonly BEARISH_KEYWORDS = [
    'sell',
    'bearish',
    'crash',
    'down',
    'loss',
    'short',
    'puts',
    'dump',
    'breakdown',
    'falling',
    'weak',
    'decline',
    'drop',
  ];

  private readonly NEGATION_WORDS = [
    "don't",
    'dont',
    'not',
    'no',
    'never',
    'neither',
    'nobody',
    'nothing',
    'nowhere',
    'cant',
    "can't",
    'cannot',
    "won't",
    'wont',
    "shouldn't",
    'shouldnt',
    "wouldn't",
    'wouldnt',
  ];

  /**
   * Analyze sentiment of a single message
   * Returns 'bullish', 'bearish', or 'neutral'
   */
  private analyzeSentiment(content: string): 'bullish' | 'bearish' | 'neutral' {
    const lowerContent = content.toLowerCase();
    const words = lowerContent.split(/\s+/);

    let bullishScore = 0;
    let bearishScore = 0;

    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const prevWord = i > 0 ? words[i - 1] : '';
      const isNegated = this.NEGATION_WORDS.includes(prevWord);

      // Check bullish keywords
      if (this.BULLISH_KEYWORDS.includes(word)) {
        if (isNegated) {
          bearishScore++; // Negation flips sentiment
        } else {
          bullishScore++;
        }
      }

      // Check bearish keywords
      if (this.BEARISH_KEYWORDS.includes(word)) {
        if (isNegated) {
          bullishScore++; // Negation flips sentiment
        } else {
          bearishScore++;
        }
      }
    }

    // Determine overall sentiment
    if (bullishScore > bearishScore) {
      return 'bullish';
    } else if (bearishScore > bullishScore) {
      return 'bearish';
    } else {
      return 'neutral';
    }
  }

  /**
   * Get sentiment for a specific ticker within a group
   *
   * @param groupId - Group ID
   * @param ticker - Stock ticker symbol (e.g., "AAPL")
   * @param timeframe - Time window ("24h", "7d", "30d")
   * @param includeAI - Whether to include AI messages in sentiment calculation (default: true)
   * @returns Ticker sentiment breakdown
   */
  async getTickerSentiment(
    groupId: string,
    ticker: string,
    timeframe: '24h' | '7d' | '30d' = '7d',
    includeAI: boolean = true
  ): Promise<{ data: TickerSentiment | null; error: string | null }> {
    try {
      console.log(
        `[TickerSentiment] Analyzing sentiment for ${ticker} in group ${groupId} (timeframe: ${timeframe})`
      );

      // Calculate start date based on timeframe
      const now = new Date();
      const startDate = new Date();
      switch (timeframe) {
        case '24h':
          startDate.setHours(now.getHours() - 24);
          break;
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
      }

      // Fetch messages that mention the ticker (in content or metadata)
      const { data: messages, error: messagesError } = await supabase
        .from('messages')
        .select('*')
        .eq('group_id', groupId)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      if (messagesError) {
        console.error(`[TickerSentiment] Error fetching messages:`, messagesError);
        return { data: null, error: messagesError.message };
      }

      if (!messages || messages.length === 0) {
        console.log(`[TickerSentiment] No messages found in group ${groupId}`);
        return {
          data: {
            ticker,
            bullishCount: 0,
            bearishCount: 0,
            neutralCount: 0,
            totalMentions: 0,
            sentiment: 'neutral',
            confidenceScore: 0,
            recentMessages: [],
            timeframe,
          },
          error: null,
        };
      }

      // Filter messages that mention the ticker
      const normalizedTicker = ticker.toUpperCase();
      const tickerMessages = messages.filter(msg => {
        const normalizedContent = msg.content.toUpperCase();

        // Check if ticker is in content (with or without $)
        const inContent =
          normalizedContent.includes(`$${normalizedTicker}`) ||
          normalizedContent.includes(normalizedTicker);

        // Check if ticker is in metadata (extracted, inherited, or manual tickers)
        const metadata = msg.metadata as any;
        const inMetadata =
          metadata?.extracted_tickers?.some((t: string) => t.toUpperCase() === normalizedTicker) ||
          metadata?.inherited_tickers?.some((t: string) => t.toUpperCase() === normalizedTicker) ||
          metadata?.manual_tickers?.some((t: string) => t.toUpperCase() === normalizedTicker);

        return inContent || inMetadata;
      });

      console.log(`[TickerSentiment] Found ${tickerMessages.length} messages mentioning ${ticker}`);

      // Filter out AI messages if includeAI is false
      let filteredMessages = tickerMessages;
      if (!includeAI) {
        const beforeCount = tickerMessages.length;
        filteredMessages = tickerMessages.filter(msg => {
          const metadata = msg.metadata as any;
          return metadata?.isAI !== true;
        });
        console.log(
          `[TickerSentiment] Filtered ${beforeCount} → ${filteredMessages.length} messages (human-only)`
        );
      }

      if (filteredMessages.length === 0) {
        return {
          data: {
            ticker,
            bullishCount: 0,
            bearishCount: 0,
            neutralCount: 0,
            totalMentions: 0,
            sentiment: 'neutral',
            confidenceScore: 0,
            recentMessages: [],
            timeframe,
          },
          error: null,
        };
      }

      // Analyze sentiment of each message
      let bullishCount = 0;
      let bearishCount = 0;
      let neutralCount = 0;

      for (const msg of filteredMessages) {
        const sentiment = this.analyzeSentiment(msg.content);
        if (sentiment === 'bullish') {
          bullishCount++;
        } else if (sentiment === 'bearish') {
          bearishCount++;
        } else {
          neutralCount++;
        }
      }

      // Determine overall sentiment
      const totalMentions = filteredMessages.length;
      let overallSentiment: 'bullish' | 'bearish' | 'neutral';
      let confidenceScore: number;

      if (bullishCount > bearishCount) {
        overallSentiment = 'bullish';
        confidenceScore = Math.round((bullishCount / totalMentions) * 100);
      } else if (bearishCount > bullishCount) {
        overallSentiment = 'bearish';
        confidenceScore = Math.round((bearishCount / totalMentions) * 100);
      } else {
        overallSentiment = 'neutral';
        confidenceScore = Math.round((neutralCount / totalMentions) * 100);
      }

      // Get recent messages (last 5-10)
      const recentMessages = filteredMessages.slice(0, 10);

      console.log(
        `[TickerSentiment] ${ticker}: ${bullishCount} bullish, ${bearishCount} bearish, ${neutralCount} neutral → ${overallSentiment} (${confidenceScore}%)`
      );

      return {
        data: {
          ticker,
          bullishCount,
          bearishCount,
          neutralCount,
          totalMentions,
          sentiment: overallSentiment,
          confidenceScore,
          recentMessages,
          timeframe,
        },
        error: null,
      };
    } catch (error) {
      console.error(`[TickerSentiment] Error analyzing sentiment:`, error);
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

// Export singleton instance
export const tickerSentimentService = new TickerSentimentService();
